interface ColorOptions {
  saturation?: { min: number; max: number };
  lightness?: { min: number; max: number };
  format?: 'hsl' | 'hex' | 'rgb';
  seed?: number; // for consistent colors
}

export const generateRandomColors = (
  count: number,
  options: ColorOptions = {},
): string[] => {
  const {
    saturation = { min: 30, max: 60 }, // Lower saturation for pastel colors
    lightness = { min: 60, max: 80 }, // Higher lightness for pastel colors
    format = 'hsl',
    seed,
  }: ColorOptions = options;

  // Use seed for consistent colors if provided
  let random = Math.random;
  if (seed !== undefined) {
    let seedValue = seed;
    random = () => {
      const x = Math.sin(seedValue++) * 10000;
      return x - Math.floor(x);
    };
  }

  const colors: string[] = [];
  const hueStep = 360 / count; // Distribute hues evenly for better color separation

  for (let i = 0; i < count; i++) {
    const baseHue = (i * hueStep) % 360;
    const hueVariation = (random() - 0.5) * 30; // Add some randomness but keep colors distinct
    const hue = Math.floor(baseHue + hueVariation + 360) % 360;

    const sat = Math.floor(
      random() * (saturation.max - saturation.min) + saturation.min,
    );
    const light = Math.floor(
      random() * (lightness.max - lightness.min) + lightness.min,
    );

    let color: string;
    switch (format) {
      case 'hex':
        color = hslToHex(hue, sat, light);
        break;
      case 'rgb':
        color = hslToRgb(hue, sat, light);
        break;
      default:
        color = `hsl(${hue}, ${sat}%, ${light}%)`;
    }

    colors.push(color);
  }

  return colors;
};

// Helper function to convert HSL to Hex
const hslToHex = (h: number, s: number, l: number): string => {
  const { r, g, b } = hslToRgbValues(h, s, l);
  return `#${[r, g, b]
    .map((x) => {
      const hex = Math.round(x).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    })
    .join('')}`;
};

// Helper function to convert HSL to RGB string
const hslToRgb = (h: number, s: number, l: number): string => {
  const { r, g, b } = hslToRgbValues(h, s, l);
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

// Helper function to convert HSL to RGB values
const hslToRgbValues = (h: number, s: number, l: number) => {
  h /= 360;
  s /= 100;
  l /= 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  return { r: r * 255, g: g * 255, b: b * 255 };
};

// Generate consistent colors for the same data (useful for charts)
export const generateConsistentColors = (
  data: any[],
  keyField: string,
  options: ColorOptions = {},
): string[] => {
  const customColKeys = [...new Set(data.map((item) => item[keyField]))];
  const colors = generateRandomColors(customColKeys.length, options);

  return data.map((item) => {
    const index = customColKeys.indexOf(item[keyField]);
    return colors[index];
  });
};
